import { UserPlusIcon } from "@phosphor-icons/react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useNavigate, useRouter } from "@tanstack/react-router";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button/Button";
import { Drawer, DrawerContent } from "@/components/Drawer";
import DeleteTraveller from "@/features/travellers/components/DeleteTraveller";
import { TravellerFormDrawer } from "@/features/travellers/components/TravellerFormDrawer";
import {
  travellersQueryOptions,
  useDeleteTravellerMutation,
} from "@/features/travellers/hooks/useTravellers";
import type { Traveller } from "@/features/travellers/types/types";
import { numericDateFormatOptions } from "@/utils/dates";

import { TravelerTypeLabel } from "./TravelerTypeLabel";
import { TravellerActions } from "./TravellerActions";

interface Props {
  edit?: string;
  delete?: string;
  new?: boolean;
}

export function TravellerListV2(props: Props) {
  const navigate = useNavigate();
  const intl = useIntl();

  const { mutateAsync: deleteTraveller } = useDeleteTravellerMutation();
  const { history } = useRouter();

  const { data } = useSuspenseQuery(travellersQueryOptions());

  const initTravellerDeletion = (traveller: Traveller) => {
    navigate({ to: "/travelers", search: { delete: traveller.id } });
  };

  const initTravellerEdit = (traveller: Traveller) => {
    navigate({ to: "/travelers", search: { edit: traveller.id } });
  };

  const closeDrawerHandler = () => {
    history.back();
  };

  const travellerForDeletion = props.delete
    ? data?.find((t) => t.id === props.delete)
    : undefined;
  const travellerForEdit = props.edit
    ? data?.find((t) => t.id === props.edit)
    : undefined;

  return (
    <>
      <div className="section-card">
        <ul className="-my-4 divide-y divide-stroke-neutral-soft *:py-4">
          {data?.map((traveller) => (
            <li key={traveller.id} className="flex justify-between">
              <div className="flex flex-1 flex-col" data-sentry-mask>
                <span className="text-base font-medium text-text-primary">
                  {traveller.firstName} {traveller.lastName}
                </span>
                <span className="text-sm text-text-secondary">
                  <TravelerTypeLabel {...traveller} />{" "}
                  <span className="text-stroke-neutral-medium">&bull;</span>{" "}
                  {intl.formatDate(
                    traveller.birthDate,
                    numericDateFormatOptions,
                  )}
                </span>
              </div>

              <div className="flex shrink-0 items-center">
                <TravellerActions
                  traveller={traveller}
                  onDelete={initTravellerDeletion}
                  onEdit={initTravellerEdit}
                />
              </div>
            </li>
          ))}
        </ul>
      </div>
      <div className="flex justify-center">
        <div className="my-2">
          <Button
            variant="text"
            size="small"
            onClick={() => {
              navigate({ to: "/travelers", search: { new: true } });
            }}
            color="primary"
          >
            <UserPlusIcon size={24} className="me-2 fill-text-interactive" />
            {intl.formatMessage({
              defaultMessage: "Add new traveller",
              description: "Button to create a new traveller profile",
            })}
          </Button>
        </div>
      </div>

      <TravellerFormDrawer
        open={Boolean(props.new || travellerForEdit)}
        onClose={() => {
          closeDrawerHandler();
        }}
        traveller={travellerForEdit || null}
      />

      <Drawer open={Boolean(travellerForDeletion)}>
        <DrawerContent>
          {travellerForDeletion ? (
            <DeleteTraveller
              traveller={travellerForDeletion}
              deleteTraveller={() => deleteTraveller(travellerForDeletion.id)}
              closeDrawer={closeDrawerHandler}
            />
          ) : null}
        </DrawerContent>
      </Drawer>
    </>
  );
}
